import { ENABLED_LANGUAGES, FALLBACK_LANGUAGE } from '@app/i18n'

export type SupportedLocale = typeof ENABLED_LANGUAGES[number]

/**
 * Extract locale from URL path
 * @example getLocaleFromPath('/ru/catalog') => 'ru'
 * @example getLocaleFromPath('/catalog') => null
 */
export function getLocaleFromPath (path: string): SupportedLocale | null {
  const segments = path.split('/').filter(Boolean)
  const firstSegment = segments[0]

  if (firstSegment && (ENABLED_LANGUAGES as readonly string[]).includes(firstSegment)) {
    return firstSegment as SupportedLocale
  }

  return null
}

/**
 * Remove locale prefix from path
 * @example stripLocaleFromPath('/ru/catalog') => '/catalog'
 * @example stripLocaleFromPath('/catalog') => '/catalog'
 */
export function stripLocaleFromPath (path: string): string {
  const locale = getLocaleFromPath(path)

  if (locale) {
    const withoutLocale = path.replace(new RegExp(`^/${locale}(?=/|$)`), '')
    return withoutLocale || '/'
  }

  return path
}

/**
 * Add locale prefix to path
 * @example localizeUrl('/catalog', 'ru') => '/ru/catalog'
 * @example localizeUrl('/ru/catalog', 'kk') => '/kk/catalog'
 */
export function localizeUrl (path: string, locale: SupportedLocale): string {
  // Strip any existing locale
  const cleanPath = stripLocaleFromPath(path)

  // Extract search params and hash
  const url = new URL(cleanPath, 'http://localhost')
  const pathname = url.pathname
  const search = url.search
  const hash = url.hash

  // Add locale prefix
  const localizedPath = pathname === '/' ? `/${locale}` : `/${locale}${pathname}`

  return `${localizedPath}${search}${hash}`
}

/**
 * Get default locale
 */
export function getDefaultLocale (): SupportedLocale {
  return FALLBACK_LANGUAGE as SupportedLocale
}

/**
 * Check if locale is valid
 */
export function isValidLocale (locale: string): locale is SupportedLocale {
  return (ENABLED_LANGUAGES as readonly string[]).includes(locale)
}
